import { API_URL, AUTH0_CLIENT_ID, AUTH0_DOMAIN } from '../constants'

/**
 * Auto-login after registration (see ADR / signup feature).
 *
 * Registration creates the Auth0 user via the Management API, which does NOT
 * establish a session — normally the user is then bounced to Universal Login to
 * type the password they just set. This helper skips that: it exchanges the
 * just-entered credentials for tokens via Auth0's Resource Owner Password Grant
 * (password-realm variant) and seeds them into the `@auth0/auth0-spa-js` cache,
 * so the SDK adopts the session on the next full page load and the user lands
 * straight in the app.
 *
 * Why ROPG + cache-seeding rather than something cleaner:
 *  - The whole app gates on the Auth0 SDK's own session (`isAuthenticated`,
 *    `getAccessTokenSilently`, `getIdTokenClaims` in AppAuthorizationChecker).
 *    A token returned from our API would not populate that — only a cache entry
 *    the SDK recognises does.
 *  - We use the public SPA `client_id` so the refresh token is bound to the same
 *    client the SDK uses for rotation; the SDK's normal silent-refresh then keeps
 *    working after the access token expires.
 *
 * Requirements on the Auth0 tenant (see docs/auth/auto-login-after-signup.md):
 *  - The SPA client must have the Password grant enabled.
 *  - The API (audience) must allow offline access, so a refresh token is issued.
 *
 * Fails safe: any error (grant disabled, wrong credentials, an MFA-enrolled user
 * triggering an interactive challenge, network) returns `false`, and the caller
 * falls back to the standard `/login` redirect. Fresh signups never have MFA, so
 * the happy path is clean; this is also why deploying ahead of enabling the grant
 * in a given environment is safe.
 *
 * @returns `true` if a session was seeded and the caller should route into the
 *   app (via a full reload); `false` to fall back to the login redirect.
 */
export async function attemptAutoLoginAfterSignup(
  email: string,
  password: string
): Promise<boolean> {
  try {
    const response = await fetch(`https://${AUTH0_DOMAIN}/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        grant_type: 'http://auth0.com/oauth/grant-type/password-realm',
        realm: 'Username-Password-Authentication',
        username: email,
        password,
        client_id: AUTH0_CLIENT_ID,
        audience: API_URL,
        scope: AUTH0_CACHE_SCOPE,
      }),
    })

    if (!response.ok) return false

    const token = (await response.json()) as Auth0TokenResponse
    if (!token.access_token || !token.id_token) return false

    seedAuth0SpaCache(token)
    return true
  } catch {
    return false
  }
}

interface Auth0TokenResponse {
  access_token: string
  id_token: string
  refresh_token?: string
  scope?: string
  expires_in: number
  token_type: string
}

/**
 * The exact scope string `@auth0/auth0-spa-js@1.22.6` uses to key its cache when
 * the app is configured with `useRefreshTokens: true` and no custom `scope`
 * (which is how <Auth0Provider> is set up in App.tsx). The SDK computes it as
 * getUniqueScopes(defaultScope, instanceScope) =
 * getUniqueScopes('openid profile email', 'offline_access'). The string (and its
 * ordering) must match the SDK's exactly or it will not find the seeded entry.
 */
export const AUTH0_CACHE_SCOPE = 'openid profile email offline_access'

const CACHE_KEY_PREFIX = '@@auth0spajs@@'

/**
 * Write the token response into the localStorage cache in the exact
 * `WrappedCacheEntry` shape auth0-spa-js@1.22.6 expects, under the key
 * `@@auth0spajs@@::<client_id>::<audience>::<scope>`. No key-manifest entry is
 * needed because LocalStorageCache enumerates keys via allKeys().
 */
function seedAuth0SpaCache(token: Auth0TokenResponse): void {
  const claims = decodeJwtPayload(token.id_token)

  const key = `${CACHE_KEY_PREFIX}::${AUTH0_CLIENT_ID}::${API_URL}::${AUTH0_CACHE_SCOPE}`

  const nowSeconds = Math.floor(Date.now() / 1000)
  // Mirrors the SDK's wrapCacheEntry: cap the access-token lifetime by the
  // id_token's own exp so a stale id_token never outlives its access token.
  const expiresAt = Math.min(nowSeconds + token.expires_in, claims.exp)

  const wrappedEntry = {
    body: {
      id_token: token.id_token,
      access_token: token.access_token,
      ...(token.refresh_token
        ? { refresh_token: token.refresh_token }
        : undefined),
      expires_in: token.expires_in,
      decodedToken: {
        claims: { ...claims, __raw: token.id_token },
        user: toUserProfile(claims),
      },
      audience: API_URL,
      scope: AUTH0_CACHE_SCOPE,
      client_id: AUTH0_CLIENT_ID,
      ...(token.scope ? { oauthTokenScope: token.scope } : undefined),
    },
    expiresAt,
  }

  localStorage.setItem(key, JSON.stringify(wrappedEntry))
}

/**
 * Build the SDK's `user` object from id_token claims: the full payload minus the
 * JWT-mechanics claims the SDK strips in getUser(). Profile/custom claims
 * (sub, email, name, coreApi/*) are kept.
 */
function toUserProfile(
  claims: Record<string, unknown>
): Record<string, unknown> {
  const user = { ...claims }
  for (const mechanic of [
    'iss',
    'aud',
    'exp',
    'nbf',
    'iat',
    'jti',
    'azp',
    'nonce',
    'auth_time',
    'at_hash',
    'c_hash',
    'acr',
    'amr',
    'sid',
  ]) {
    delete user[mechanic]
  }
  return user
}

/** Decode a JWT payload (base64url) without pulling in a dependency. */
function decodeJwtPayload(jwt: string): Record<string, any> {
  const payload = jwt.split('.')[1]
  const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  )
  return JSON.parse(json)
}
